import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const field = "w-full h-12 px-4 rounded-lg border border-sand bg-white text-ink focus:outline-none focus:ring-2 focus:ring-tea-hover/70 transition-shadow duration-200";

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Those passwords don't match.");
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      toast.success("Password reset. Log in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6 font-body text-ink" data-testid="reset-password-page">
      <div className="w-full max-w-sm animate-rise">
        {!token ? (
          <div className="text-center">
            <p className="font-heading font-bold text-2xl">Link invalid or incomplete</p>
            <p className="text-ink/60 mt-2 [text-wrap:pretty]">
              This password reset link is missing its token. Request a new one below.
            </p>
            <a href="/forgot-password" className="inline-block mt-6 text-sm text-tea-text hover:underline">Request a new link</a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight">Choose a new password</h2>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/60">New password</label>
              <input required type="password" data-testid="reset-password-new-input" className={`${field} mt-1`} value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/60">Confirm new password</label>
              <input required type="password" data-testid="reset-password-confirm-input" className={`${field} mt-1`} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <button disabled={busy} data-testid="reset-password-submit"
              className="w-full h-12 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-[background-color,transform] duration-200 active:scale-[0.96] disabled:opacity-60 disabled:active:scale-100">
              {busy ? "Please wait…" : "Reset password"}
            </button>
            {failed && (
              <p className="text-sm text-ink/60 text-center [text-wrap:pretty]">
                That link may be expired or invalid. <a href="/forgot-password" className="underline hover:text-ink/80">Request a new one</a>.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
